import PropTypes from 'prop-types';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { io } from 'socket.io-client';
import { SERVER_URL } from '../config';
import { useAuth } from './useAuth';

// Moguca stanja veze koja UI koristi za "Povezivanje..." indikator.
export const SOCKET_STATUS = {
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',
};

const SocketContext = createContext({
  socket: null,
  status: SOCKET_STATUS.DISCONNECTED,
  connected: false,
});

// Jedan socket po sesiji. Otvara se kada postoji token (posle logina), a gasi se
// na logout (kada token nestane). Nikada se ne otvara po komponenti.
export const SocketProvider = ({ children }) => {
  const { token } = useAuth();
  const [socket, setSocket] = useState(null);
  const [status, setStatus] = useState(SOCKET_STATUS.DISCONNECTED);

  useEffect(() => {
    if (!token) {
      setSocket(null);
      setStatus(SOCKET_STATUS.DISCONNECTED);
      return undefined;
    }

    setStatus(SOCKET_STATUS.CONNECTING);
    const instance = io(SERVER_URL, {
      auth: { token },
      // Podrazumevani reconnection je ukljucen; UI prati status ispod.
    });

    const handleConnect = () => setStatus(SOCKET_STATUS.CONNECTED);
    const handleDisconnect = () => setStatus(SOCKET_STATUS.DISCONNECTED);
    const handleConnecting = () => setStatus(SOCKET_STATUS.CONNECTING);

    instance.on('connect', handleConnect);
    instance.on('disconnect', handleDisconnect);
    instance.on('connect_error', handleConnecting);
    // Reconnection dogadjaji zive na manager-u (instance.io).
    instance.io.on('reconnect_attempt', handleConnecting);
    instance.io.on('reconnect', handleConnect);

    setSocket(instance);

    return () => {
      instance.off('connect', handleConnect);
      instance.off('disconnect', handleDisconnect);
      instance.off('connect_error', handleConnecting);
      instance.io.off('reconnect_attempt', handleConnecting);
      instance.io.off('reconnect', handleConnect);
      instance.disconnect();
      setSocket(null);
      setStatus(SOCKET_STATUS.DISCONNECTED);
    };
  }, [token]);

  const value = useMemo(
    () => ({
      socket,
      status,
      connected: status === SOCKET_STATUS.CONNECTED,
    }),
    [socket, status]
  );

  return (
    <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
  );
};

SocketProvider.propTypes = {
  children: PropTypes.node,
};

export const useSocket = () => useContext(SocketContext);
